import React, { useState } from "react";
import { Download, MonitorSmartphone, Share, X, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import { useInstallPrompt } from "../../lib/pwa";

const DISMISS_KEY = "kt_install_banner_dismissed";

export function InstallButton({ className = "" }) {
  const { canInstall, install, installed, ios } = useInstallPrompt();
  const [iosHint, setIosHint] = useState(false);
  if (installed) return null;
  if (!canInstall && !ios) return null;
  return (
    <div className={className}>
      <button type="button" onClick={() => (canInstall ? install() : setIosHint((v) => !v))} className="flex w-full items-center gap-3 rounded-xl border border-dashed border-brand-200 bg-brand-50/60 px-3 py-2.5 text-left text-sm font-medium text-brand transition-colors hover:bg-brand-50" data-testid="install-app-button">
        <Download className="h-[18px] w-[18px] shrink-0" />
        <span className="flex-1 leading-tight">Install app<span className="block text-[11px] font-normal text-ink-muted">Use on this computer or tablet</span></span>
      </button>
      {iosHint && <IosHint className="mt-2" />}
    </div>
  );
}

export function IosHint({ className = "" }) {
  return (
    <p className={`rounded-xl bg-white px-3 py-2 text-xs text-ink-soft ring-1 ring-surface-300 ${className}`} data-testid="ios-install-hint">
      On iPad: tap <Share className="inline h-3.5 w-3.5 align-text-bottom" /> <strong>Share</strong> in Safari, then <strong>Add to Home Screen</strong>.
    </p>
  );
}

export function InstallBanner() {
  const { canInstall, install, installed, ios } = useInstallPrompt();
  const [hidden, setHidden] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  const [done, setDone] = useState(false);
  if (hidden || installed || (!canInstall && !ios && !done)) return null;
  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setHidden(true); };
  const go = async () => { if (await install()) setDone(true); };
  return (
    <div className="mb-6 flex flex-wrap items-center gap-4 rounded-2xl border border-graphite-200 bg-graphite-50 p-4" data-testid="install-banner">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-graphite text-white"><MonitorSmartphone className="h-5 w-5" /></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">Run the orders board as an app on this {ios ? "iPad" : "computer or tablet"}</p>
        <p className="text-xs text-ink-soft">Own window, home-screen icon, opens straight to Orders. Updates automatically.</p>
        {ios && <IosHint className="mt-2" />}
      </div>
      {done ? <span className="inline-flex items-center gap-1 text-sm font-semibold text-graphite"><CheckCircle2 className="h-4 w-4" /> Installed</span> : canInstall && (
        <Button onClick={go} className="rounded-full bg-graphite text-white hover:bg-graphite-600" data-testid="install-banner-button"><Download className="mr-2 h-4 w-4" /> Install app</Button>
      )}
      <button type="button" onClick={dismiss} className="rounded-lg p-2 text-ink-muted hover:bg-white hover:text-ink" aria-label="Dismiss" data-testid="install-banner-dismiss"><X className="h-4 w-4" /></button>
    </div>
  );
}
